import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Manages a `Set<string>` of selected ids, scoped to a list of currently-visible
 * ids. When the visible set changes (e.g. the user switches projects, refreshes,
 * or filters), the selection is automatically intersected with the new visible
 * set — so a row that disappears from view stops being selected, and re-rendered
 * rows stay selected if their ids are still present.
 *
 * Returned helpers cover the typical bulk-action UI needs: per-row toggle,
 * master toggle (select-all-visible / clear), state queries (`isSelected`,
 * `isAllSelected`, `isPartial`), and an explicit `clear`.
 */
export interface SelectionApi {
  selectedIds: Set<string>;
  count: number;
  isSelected: (id: string) => boolean;
  isAllSelected: boolean;
  isPartial: boolean;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
}

export function useSelection(visibleIds: readonly string[]): SelectionApi {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // Intersect whenever the visible set changes content.
  const visibleKey = useMemo(() => visibleIds.join(' '), [visibleIds]);

  // Key the effect on the joined string only; visibleIds itself is a new
  // array every render but its content rarely changes. Including visibleIds
  // would re-fire on every render and waste work.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleKey.split(' ').filter(Boolean));
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [visibleKey]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      // If everything visible is already selected, clear; otherwise select all visible.
      if (visibleIds.length === 0) return prev;
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const isAllSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const isPartial = !isAllSelected && selectedIds.size > 0;

  return {
    selectedIds,
    count: selectedIds.size,
    isSelected,
    isAllSelected,
    isPartial,
    toggle,
    toggleAll,
    clear,
  };
}
